import { useEffect, useId, useState } from 'react'
import { Button } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconPrescription } from '@tabler/icons-react'
import type { Patient } from '@medplum/fhirtypes'
import { useDoseSpotPrescriptionSync } from '@/hooks/useDoseSpotPrescriptionSync'
import { claimDoseSpotLaunch, releaseDoseSpotLaunch } from '@/utils/doseSpotLaunchLock'
import { DoseSpotPrescribeModal } from './DoseSpotPrescribeModal'

type Props = {
  patient: Patient
  /** From the call route's own match, so its presence also means "in a visit". */
  encounterId?: string
  onPatientEdited?: () => void
}

/**
 * The one Prescribe affordance. It lives in the shared patient action bar, so the
 * chart and the live call get the same control and the same modal — there is no
 * second prescribing path to keep in step.
 *
 * The control renders for everyone and the *click* decides: there is one mint-only
 * endpoint and no eligibility query, so a viewer with no DoseSpot Clinician Mapping
 * learns it from the modal's blocked-provider state rather than from a hidden or
 * disabled button. Role is deliberately not consulted here — a proxy or prescribing
 * agent can hold a manager role (PD-1592), and the server's mapping gate is the one
 * that decides.
 */
export const DoseSpotPrescribeControl = ({ patient, encounterId, onPatientEdited }: Props) => {
  const syncPrescriptions = useDoseSpotPrescriptionSync()
  const [isOpen, setIsOpen] = useState(false)
  // Stable per mounted control, which is what the launch slot is keyed on.
  const claimId = useId()

  // A route change unmounts the chart with the modal open; the slot must not
  // outlive it or Prescribe stays wedged for the rest of the session.
  useEffect(() => () => releaseDoseSpotLaunch(claimId), [claimId])

  const openModal = () => {
    if (!claimDoseSpotLaunch(claimId)) {
      showNotification({
        color: 'orange',
        message:
          'Close the open DoseSpot window first — DoseSpot can only hold one prescribing session at a time.',
      })
      return
    }
    setIsOpen(true)
  }

  const closeModal = () => {
    setIsOpen(false)
    releaseDoseSpotLaunch(claimId)
    // The write-back trigger. It lives here rather than in the modal because closing
    // unmounts the modal, which would cancel a request fired from inside it.
    //
    // Undebounced: a close is an explicit action that may just have produced a
    // prescription, and "the iframe closed" is not otherwise an observable event —
    // the frame is cross-origin, so BeHuman's own close button is the only signal
    // there is. Fired for every close, including one that follows a refusal: the
    // handler answers instantly for a patient DoseSpot has never seen, and a launch
    // that failed today says nothing about prescriptions written yesterday.
    if (patient.id) syncPrescriptions(patient.id, { notifyOnFailure: true })
  }

  return (
    <>
      <Button
        variant="filled"
        color="blue"
        leftSection={<IconPrescription size={16} />}
        onClick={openModal}
      >
        Prescribe
      </Button>
      {isOpen && (
        <DoseSpotPrescribeModal
          patient={patient}
          encounterId={encounterId}
          onClose={closeModal}
          onPatientEdited={onPatientEdited}
        />
      )}
    </>
  )
}
